import { useMemo, useState } from "react";
import { Box, Button } from "@mui/material";
import AsyncSelect from "@/components/AsyncSelect";
import Flex from "@/components/Flex";
import Text from "@/components/Text";
import PatientLabPharmacyRadiologyFormDrawer from "@/components/app/provider/patient/add-new-patient/preferences/PatientLabPharmacyRadiologyFormDrawer";
import useDisclosure from "@/hooks/use-disclosure";
import { useSnackbar } from "@/hooks/useSnackbar";
import EntryCard from "./EntryCard";
import { usePharmacyDirectory } from "../hooks/use-pharmacy-directory";
import {
  ADD_PHARMACY_LABEL,
  EDIT_ENTRY_LABEL,
  EDIT_PHARMACY_SUBMIT_LABEL,
  EDIT_PHARMACY_TITLE,
  PHARMACY_HINT,
  PHARMACY_LABELS,
  PHARMACY_PREFERENCE_TYPE,
  PHARMACY_RECORD_NAME,
  PHARMACY_SELECT_PLACEHOLDER,
  REMOVE_ENTRY_LABEL,
} from "../readmission-f2f-constant";
import { PharmacyEntry } from "../readmission-f2f-types";
import { newId, recordChangedSnackbar } from "../readmission-f2f-utils";
import {
  applyPharmacyEdit,
  createPharmacyEntryFromPreference,
  toPharmacyFormValues,
} from "../readmission-f2f-pharmacy-utils";

export interface PharmacyEntryListProps {
  items: PharmacyEntry[];
  onChange: (items: PharmacyEntry[]) => void;
  isEnabled: boolean;
}

const PharmacyDetails = ({ entry }: { entry: PharmacyEntry }) => (
  <Box display="flex" flexDirection="column" gap={0.5}>
    <Text fontWeight={600}>{entry.name}</Text>
    {[
      [PHARMACY_LABELS.phone, entry.phone],
      [PHARMACY_LABELS.fax, entry.fax],
      [PHARMACY_LABELS.address, entry.address],
    ]
      .filter(([, value]) => value.trim())
      .map(([label, value]) => (
        <Text key={label} size={13}>
          {label}: {value}
        </Text>
      ))}
  </Box>
);

const PharmacyEntryList = ({
  items,
  onChange,
  isEnabled,
}: PharmacyEntryListProps) => {
  const { pharmacies, isLoading, setSearch } = usePharmacyDirectory(isEnabled);
  const showSnackbar = useSnackbar();
  const addDrawer = useDisclosure();
  const [editingId, setEditingId] = useState<string>();
  const editing = items.find((it) => it.id === editingId);
  const editValues = useMemo(
    () => (editing ? toPharmacyFormValues(editing) : undefined),
    [editing]
  );

  const options = pharmacies
    .filter((it) => !items.some((item) => item.uuid && item.uuid === it.uuid))
    .map((it) => ({ value: it.id, label: it.name }));

  const append = (entry: PharmacyEntry) => onChange([...items, entry]);

  const add = (entry: PharmacyEntry) => {
    append(entry);
    showSnackbar(recordChangedSnackbar(PHARMACY_RECORD_NAME, "added"));
  };

  const replace = (entry: PharmacyEntry) =>
    onChange(items.map((it) => (it.id === entry.id ? entry : it)));

  const update = (entry: PharmacyEntry) => {
    replace(entry);
    showSnackbar(recordChangedSnackbar(PHARMACY_RECORD_NAME, "updated"));
  };

  const remove = (entry: PharmacyEntry) => {
    onChange(items.filter((it) => it.id !== entry.id));
    showSnackbar(recordChangedSnackbar(PHARMACY_RECORD_NAME, "removed"));
  };

  return (
    <Box display="flex" flexDirection="column" gap={1.5}>
      {items.map((item) => (
        <EntryCard
          key={item.id}
          removeLabel={`${REMOVE_ENTRY_LABEL} ${item.name}`}
          onRemove={() => remove(item)}
          editLabel={`${EDIT_ENTRY_LABEL} ${item.name}`}
          onEdit={() => setEditingId(item.id)}
        >
          <PharmacyDetails entry={item} />
        </EntryCard>
      ))}
      <EntryCard>
        <AsyncSelect
          // Remounts after each change so the picked name doesn't linger in the input.
          key={items.length}
          placeholder={PHARMACY_SELECT_PLACEHOLDER}
          loading={isLoading}
          options={options}
          value={null}
          onSearch={setSearch}
          onChange={(picked) => {
            const hit = pharmacies.find((it) => it.id === picked?.value);
            if (hit) add({ ...hit, id: newId("pharmacy") });
          }}
        />
        <Flex justifyContent="flex-end">
          <Button variant="outlined" size="small" onClick={addDrawer.open}>
            {ADD_PHARMACY_LABEL}
          </Button>
        </Flex>
      </EntryCard>
      <Text size={12} c="text.secondary">
        {PHARMACY_HINT}
      </Text>
      <PatientLabPharmacyRadiologyFormDrawer
        opened={addDrawer.opened}
        handleCloseModal={addDrawer.close}
        preferenceType={PHARMACY_PREFERENCE_TYPE}
        onSaved={(values, record) =>
          append(createPharmacyEntryFromPreference(values, record))
        }
      />
      {editing && editValues && (
        <PatientLabPharmacyRadiologyFormDrawer
          opened
          handleCloseModal={() => setEditingId(undefined)}
          preferenceType={PHARMACY_PREFERENCE_TYPE}
          initialValues={editValues}
          title={EDIT_PHARMACY_TITLE}
          submitLabel={EDIT_PHARMACY_SUBMIT_LABEL}
          preferenceUuid={editing.uuid}
          onSaved={(values) => replace(applyPharmacyEdit(editing, values))}
          onSubmit={
            editing.uuid
              ? undefined
              : (values) => update(applyPharmacyEdit(editing, values))
          }
        />
      )}
    </Box>
  );
};

export default PharmacyEntryList;
